<script setup>
import { computed, reactive, ref } from "vue";
import { GqlService } from "@js/services/gql";
import AddressSearch from "@js/vue/components/AddressSearch.vue";
import FileUploader from "@js/vue/components/FileUploader.vue";

const props = defineProps({
	shipmentTypes: {
		type: Array,
		required: true,
	},
	forwarderName: String,
	shipperName: String,
	shipperId: {
		type: Number,
		required: true,
	},
	previousUrl: {
		type: String,
		required: true,
	},
	currencySymbol: {
		type: String,
		required: true,
	},
	existingInvoice: {
		type: Object,
		default: null,
	},
	itemTypes: {
		type: Array,
		default: () => [],
	},
	excludedItemsWarningText: {
		type: String,
		default: "",
	},
	// for existing expedition
	expeditionId: Number,
	origin: Object,
	destination: Object,
	transportDate: String,
	reference: String,
	shipmentType: String,
	transportCosts: Number,
	shipName: String,
	itemTitle: String,
	itemType: Number,
	itemValue: Number,
	// for sanction law
	sanctionWarningText: {
		type: String,
		default: "",
	},
	sanctionProcedureUrl: {
		type: String,
		required: true,
	},
	// whether the expedition is already approved (for existing expeditions)
	sanctionApproved: {
		type: Boolean,
		default: false,
	},
});

// Loading existing data
const craftToJsLocation = (location) => {
	if (!location) return null;

	return {
		latitude: location.lat,
		longitude: location.lng,
		fullAddress: location.address,
		number: location.parts.number,
		address: location.parts.address,
		city: location.parts.city,
		postcode: location.parts.postcode,
		county: location.parts.county,
		state: location.parts.state,
		country: location.parts.country,
	};
};

// Form data
const formData = reactive({
	expeditionId: props.expeditionId,
	shipmentType: props.shipmentType ?? props.shipmentTypes[0].value,
	shipName: props.shipName,
	originData: craftToJsLocation(props.origin),
	destinationData: craftToJsLocation(props.destination),
	transportDate: (props.transportDate
		? new Date(props.transportDate)
		: new Date()
	)
		.toISOString()
		.split("T")[0],
	reference: props.reference,
	shipperId: props.shipperId,
	transportCosts: props.transportCosts,
	itemTitle: props.itemTitle,
	itemType: props.itemType,
	itemValue: props.itemValue,
	purchaseInvoice: null,
	purchaseInvoiceChanged: false,
});

// Form state
const isSubmitting = ref(false);
const errors = ref({});
const message = ref("");
const messageType = ref(""); // 'success' or 'error'
const existingFiles = ref([]);

const createFileFromBase64 = (base64Data, filename, mimeType) => {
	if (base64Data.includes(";base64,"))
		base64Data = base64Data.split(";base64,")[1];

	const byteArray = Uint8Array.fromBase64(base64Data);
	return new File([byteArray], filename, { type: mimeType });
};

if (props.existingInvoice) {
	const file = createFileFromBase64(
		props.existingInvoice.data,
		props.existingInvoice.filename,
		props.existingInvoice.mimeType,
	);
	existingFiles.value = [file];
}

const fileToBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

const handleFileChange = async (files) => {
	let newFile = null;

	if (files.length > 0) {
		newFile = {
			fileData: await fileToBase64(files[0]),
			filename: files[0].name,
		};

		if (errors.value.purchaseInvoice) {
			delete errors.value.purchaseInvoice;
		}
	}

	formData.purchaseInvoice = newFile;
	formData.purchaseInvoiceChanged = true;
};

const getSelectedItem = computed(() => {
	if (!formData.itemType) return null;
	return props.itemTypes.find(
		(type) => parseInt(type.value) === parseInt(formData.itemType),
	);
});

const isSelectedItemExcluded = computed(() => {
	return getSelectedItem.value?.excluded === true;
});

const handleContactUs = () => {
	window.location.href = `mailto:${props.contactEmail}`;
};

// Sanction law state
const isInSanctionList = ref(false);

// Validation
const validateForm = () => {
	const newErrors = {};

	if (!formData.shipmentType) {
		newErrors.shipmentType = "Shipment type is required";
	} else if (
		formData.shipmentType.toLowerCase() == "sea" &&
		!formData.shipName
	) {
		newErrors.shipName = "Name of ship is required";
	}

	if (!formData.originData) {
		newErrors.originData = "Please select an item from the list";
	}

	if (!formData.destinationData) {
		newErrors.destinationData = "Please select an item from the list";
	}

	if (!formData.transportCosts) {
		newErrors.transportCosts = "Transport cost is required";
	}

	let today = new Date();
	today.setHours(0, 0, 0, 0);

	if (!formData.transportDate) {
		newErrors.transportDate = "Transport date is required";
	} else if (new Date(formData.transportDate) < today) {
		newErrors.transportDate = "Transport date must be today or later";
	}

	// If optional itemValue is empty, set is as null so it does not trip the GQL error: Float cannot represent non numeric value: (empty string)
	if (formData.itemValue === "") {
		formData.itemValue = null;
	}

	errors.value = newErrors;
	return Object.keys(newErrors).length === 0;
};

const currentShipmentTypeData = computed(() => {
	return props.shipmentTypes.find(
		(type) => type.value === formData.shipmentType,
	);
});

// Submit form
const handleSubmit = async () => {
	// If already flagged as sanction list violation and not approved, redirect to sanction procedure
	if (isInSanctionList.value && !props.sanctionApproved) {
		window.location.href = props.sanctionProcedureUrl;
		return;
	}

	if (!validateForm()) {
		return;
	}

	isSubmitting.value = true;
	message.value = "";

	try {
		const json = await GqlService.request(
			`
					mutation CreateExpedition(
						$expeditionId: ID,
						$shipperId: ID!,
						$shipmentType: ShipmentType!,
						$originData: LocationData!,
						$destinationData: LocationData!,
						$transportDate: DateTime!,
						$transportCosts: Float,
						$reference: String,
						$shipName: String,
						$itemTitle: String,
						$itemType: ID,
						$itemValue: Float,
						$purchaseInvoice: FileInput,
						$purchaseInvoiceChanged: Boolean,
					) {
						createExpedition(
							expeditionId: $expeditionId,
							shipperId: $shipperId,
							shipmentType: $shipmentType,
							originData: $originData,
							destinationData: $destinationData,
							transportDate: $transportDate,
							transportCosts: $transportCosts,
							reference: $reference,
							shipName: $shipName,
							itemTitle: $itemTitle,
							itemType: $itemType,
							itemValue: $itemValue,
							purchaseInvoice: $purchaseInvoice,
							purchaseInvoiceChanged: $purchaseInvoiceChanged
						) {
							id
							isInSanctionList
						}
					}
				`,
			formData,
		);

		if (json.errors) {
			message.value = json.errors[0].message;
			messageType.value = "error";

			const validationErrors = json.errors[0].extensions?.validationErrors;
			if (validationErrors) errors.value = validationErrors;

			return;
		}

		// Check if the expedition is in sanction list
		if (
			json.data.createExpedition.isInSanctionList &&
			!props.sanctionApproved
		) {
			isInSanctionList.value = true;
			// Don't redirect automatically - show warning and let user click "Contact us"
			return;
		}

		window.location.href = props.previousUrl;
	} catch (error) {
		message.value =
			error.message || "An error occurred while creating the shipper";
		messageType.value = "error";
	} finally {
		isSubmitting.value = false;
	}
};
</script>

<template>
	<div class="form-container row justify-content-center">
		<div class="col-12 col-xl-9">
			<!-- Success/Error Messages -->
			<div
				v-if="message"
				:class="[
					'alert',
					messageType === 'success' ? 'alert-success' : 'alert-danger',
				]"
				role="alert"
			>
				{{ message }}
			</div>

			<!-- Heading -->
			<h1 class="section-title text-dark-blue text-center font-medium">
				{{ expeditionId ? "Edit expedition" : "New expedition" }}
			</h1>

			<div class="section-heading text-center">
				<div class="section-text text-dark-blue" v-if="forwarderName">
					Transport company: <strong>{{ forwarderName }}</strong>
				</div>
				<div class="section-text text-dark-blue" v-if="shipperName">
					On behalf of: <strong>{{ shipperName }}</strong>
				</div>
			</div>

			<!-- Form -->
			<form class="row" @submit.prevent="handleSubmit">
				<!-- Type -->
				<div class="col-12 mb-3">
					<label class="form-label" for="shipmentType">
						Type <span class="required">*</span>
					</label>

					<div class="shipment-type-container">
						<div class="input-group">
							<div class="input-group-prepend">
								<div class="input-group-text">
									<i :class="currentShipmentTypeData.icon"></i>
								</div>
							</div>
							<select
								v-model="formData.shipmentType"
								class="form-select shipment-type"
								:class="{ 'is-invalid': errors.shipmentType }"
								id="shipmentType"
								required
							>
								<option
									v-for="type in shipmentTypes"
									:key="type"
									:value="type.value"
									:selected="type.value === formData.shipmentType"
								>
									{{ type.label }}
								</option>
							</select>
							<div v-if="errors.shipmentType" class="invalid-feedback">
								{{ errors.shipmentType }}
							</div>
						</div>
					</div>
				</div>

				<!-- Ship name - conditional -->
				<div class="col-12 mb-3" v-if="formData.shipmentType === 'sea'">
					<label class="form-label" for="shipName">
						Name of ship <span class="required">*</span>
					</label>

					<input
						class="form-control shipment-reference"
						:class="{ 'is-invalid': errors.shipName }"
						type="text"
						v-model="formData.shipName"
						placeholder="Fill out name of ship"
						id="shipName"
						required
					/>
				</div>

				<!-- Origin -->
				<div class="col-12 mb-3">
					<label class="form-label" for="originData">
						Origin <span class="required">*</span>
					</label>

					<address-search
						v-model:address-data="formData.originData"
						:is-valid="!('originData' in errors)"
						input-id="originData"
					></address-search>

					<div v-if="errors.originData" class="invalid-feedback">
						{{ errors.originData }}
					</div>
				</div>

				<!-- Destination -->
				<div class="col-12 mb-3">
					<label class="form-label" for="destinationData">
						Destination <span class="required">*</span>
					</label>

					<address-search
						v-model:address-data="formData.destinationData"
						:is-valid="!('destinationData' in errors)"
						input-id="destinationData"
					></address-search>

					<div v-if="errors.destinationData" class="invalid-feedback">
						{{ errors.destinationData }}
					</div>
				</div>

				<!-- Transport date -->
				<div class="col-12 mb-3">
					<label class="form-label" for="transportDate">
						Transport date <span class="required">*</span>
					</label>

					<input
						class="form-control"
						:class="{ 'is-invalid': errors.transportDate }"
						type="date"
						v-model="formData.transportDate"
						:min="new Date().toISOString().split('T')[0]"
						id="transportDate"
						required
					/>
					<div v-if="errors.transportDate" class="invalid-feedback">
						{{ errors.transportDate }}
					</div>
				</div>

				<!-- Transport costs -->
				<div class="col-12 mb-3">
					<label class="form-label" for="transportCosts">
						Transport costs <span class="required">*</span>
					</label>

					<div class="input-group">
						<input
							class="form-control"
							:class="{ 'is-invalid': errors.transportCosts }"
							type="number"
							min="0"
							v-model.number="formData.transportCosts"
							step="0.01"
							id="transportCosts"
							required
						/>
						<div class="input-group-append">
							<div class="input-group-text">{{ currencySymbol }}</div>
						</div>
						<div v-if="errors.transportCosts" class="invalid-feedback">
							{{ errors.transportCosts }}
						</div>
					</div>
				</div>

				<!-- Reference -->
				<div class="col-12 mb-3">
					<label class="form-label" for="reference"> Reference </label>

					<input
						class="form-control shipment-reference"
						type="text"
						v-model="formData.reference"
						placeholder="Fill out reference"
						id="reference"
					/>
				</div>

				<!-- Start of optional transported goods fields -->
				<div class="col-12 mb-3 mt-3">
					<div class="accordion" id="accordionOptional">
						<div class="accordion-item">
							<h2 class="accordion-header" id="heading1-1">
								<button
									class="accordion-button"
									type="button"
									data-bs-toggle="collapse"
									data-bs-target="#collapse1-1"
									aria-expanded="false"
									aria-controls="collapse1-1"
								>
									<strong>Details of the transported goods</strong
									>&nbsp;(optional)
								</button>
							</h2>
							<div
								id="collapse1-1"
								class="accordion-collapse show collapse"
								aria-labelledby="heading1-1"
								data-bs-parent="#accordionFaq-1"
							>
								<div class="accordion-body">
									<!-- item Title / Description of goods -->
									<div class="col-12 mb-3">
										<label class="form-label" for="itemTitle">
											Description of goods
										</label>

										<input
											class="form-control"
											type="text"
											v-model="formData.itemTitle"
											id="itemTitle"
										/>
										<div v-if="errors.itemTitle" class="invalid-feedback">
											{{ errors.itemTitle }}
										</div>
									</div>

									<!-- item Type / Category goods -->
									<div class="col-12 mb-3">
										<label class="form-label" for="itemType">
											Category goods
										</label>

										<select
											class="form-select"
											v-model="formData.itemType"
											id="itemType"
										>
											<option value="">Select Category goods</option>
											<option
												v-for="type in props.itemTypes"
												:key="type.value"
												:value="type.value"
												:selected="type.value === props.itemType"
											>
												{{ type.label }}
											</option>
										</select>
									</div>

									<!-- item Value / Total value of goods -->
									<div class="col-12 mb-3">
										<label class="form-label" for="itemValue">
											Total value of goods
										</label>
										<div class="input-group">
											<input
												class="form-control"
												type="number"
												min="0"
												v-model.number="formData.itemValue"
												step="0.01"
												id="itemValue"
											/>
											<div class="input-group-append">
												<div class="input-group-text">{{ currencySymbol }}</div>
											</div>
										</div>
									</div>

									<!-- Purchase invoice -->
									<div class="col-12 mb-3">
										<label class="form-label">Purchase invoice</label>
										<FileUploader
											:model-value="existingFiles"
											@update:model-value="handleFileChange"
											:error="errors.purchaseInvoice"
											label="Click to add a photo or document to continue."
											:max-files="1"
											:max-size="10 * 1024 * 1024"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Alert for excluded items -->
				<div v-if="isSelectedItemExcluded" class="col-12 mb-3">
					<div class="col-12">
						<div class="alert alert-warning" role="alert">
							{{
								props.excludedItemsWarningText ?? "Excluded items are selected"
							}}
						</div>
					</div>
				</div>

				<!-- Sanction Law Warning -->
				<div
					v-if="isInSanctionList && !sanctionApproved"
					class="col-12 sanction-law-alert mb-3"
				>
					<div class="alert alert-warning align-items-center d-flex gap-3">
						<i class="ri-error-warning-line h1 mb-0"></i>
						<div v-if="sanctionWarningText" v-html="sanctionWarningText"></div>
						<div v-else>
							Either origin or destination country is on the sanction law
							countries list. Please contact us to proceed.
						</div>
					</div>
				</div>

				<!-- Action Buttons -->
				<div class="row justify-content-between">
					<div class="col-auto">
						<a
							:href="previousUrl"
							class="btn btn-rounded btn-outline-blue"
							v-if="previousUrl"
						>
							<i class="ri-arrow-left-line" aria-hidden="true"></i>
							<span>Back</span>
						</a>
					</div>
					<div class="col-auto">
						<button
							v-if="isSelectedItemExcluded"
							@click="handleContactUs"
							class="btn btn-rounded btn-blue"
							type="button"
						>
							<span>Contact us</span>
							<i class="ri-arrow-right-line" aria-hidden="true"></i>
						</button>
						<button
							v-else
							class="btn btn-rounded btn-blue"
							type="submit"
							:disabled="isSubmitting"
						>
							<span v-if="isSubmitting">Saving...</span>
							<span v-else-if="isInSanctionList && !sanctionApproved"
								>Contact us</span
							>
							<span v-else>Save</span>
							<i class="ri-arrow-right-line" aria-hidden="true"></i>
						</button>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<style scoped>
.invalid-feedback {
	display: block;
	width: 100%;
	margin-top: 0.25rem;
	font-size: 0.875em;
	color: #dc3545;
}

.btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}
</style>
